<template>
  <Button
      :label="$t('label.new_project')"
      icon="far fa-plus"
      class="whitespace-nowrap rounded-lg"
      :title="$t('label.new_project')"
      @click="$router.push({ name: 'ProjectCreate' })"
  />

  <Dialog
      modal
      v-model:visible="showDialog"
      class="w-full max-w-[55rem] max-h-[95%]"
      :draggable="false"
  >
    <template #header>
      <div class="inline-flex items-center justify-center gap-2">
        <span class="font-semibold text-2xl dark:text-white">{{ $t('title.create_project') }}</span>
      </div>
    </template>

    <PrimeForm
        v-slot="$form"
        :initialValues="initialValues"
        :resolver="resolver"
        @submit="createProject"
        class="w-full"
    >
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-1">
          <label for="reference" class="block font-medium dark:text-white">
            {{ $t('label.reference') }}
          </label>

          <InputText
              id="reference"
              name="reference"
              type="text"
              class="w-full dark:bg-zinc-950"
              autofocus
          />

          <Message
              v-if="$form.reference?.invalid"
              severity="error"
              size="small"
              variant="simple"
          >
            {{ $form.reference.error.message }}
          </Message>
        </div>

        <div class="flex flex-col gap-1">
          <label for="name" class="block font-medium dark:text-white">
            {{ $t('label.project_name') }}
          </label>

          <Textarea
              id="name"
              name="name"
              rows="3"
              :auto-resize="true"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label for="project-image-upload" class="block font-medium dark:text-white">
            {{ $t('label.image') }}
          </label>

          <FileUpload
              input-id="project-image-upload"
              :pt="fileUploadPt"
              accept="image/*"
              choose-icon="far fa-plus"
              :maxFileSize="10000000"
              :auto="false"
              :show-cancel-button="false"
              :show-upload-button="false"
              @select="onFileSelect"
          >
            <template #empty>
              <span>{{ $t('message.choose_or_drag_image') }}</span>
            </template>
          </FileUpload>

          <Message
              v-if="imageError"
              severity="error"
              size="small"
              variant="simple"
          >
            {{ imageError }}
          </Message>
        </div>

        <div class="flex justify-start flex-row-reverse gap-3 mt-6">
          <Button
              type="submit"
              :label="$t('label.create')"
              icon="far fa-check"
              class="dark:text-white"
              :loading="loading"
          />

          <Button
              :label="$t('label.cancel')"
              severity="secondary"
              class="dark:text-white"
              outlined
              @click="showDialog = false"
          />
        </div>

      </div>
    </PrimeForm>
  </Dialog>
</template>

<script>
import { Form as PrimeForm } from '@primevue/forms';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import Message from 'primevue/message';
import axios from '../../utils/axios.js';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CreateProjectButton',

  components: {
    PrimeForm,
    Message
  },

  data() {
    return {
      showDialog: false,
      loading: false,

      image: null,
      imageUploadCounter: 0,
      imageError: null,

      initialValues: {
        reference: '',
        name: ''
      },

      resolver: zodResolver(
          z.object({
            reference: z
                .string()
                .min(1, { message: this.$t('validation.reference_required') }),

            name: z
                .string()
                .optional()
          })
      )
    };
  },

  computed: {
    ...mapGetters([
      'projects'
    ]),

    fileUploadPt() {
      return {
        root: {
          class: 'bg-slate-50 dark:bg-zinc-900 dark:border-zinc-600'
        },
        header: {
          class: 'px-4 pt-4 pb-0 dark:bg-zinc-900 border-none'
        },
        pcFileRemoveButton: {
          root: {
            class: 'dark:hover:bg-red-900/20 dark:active:bg-zinc-900/20'
          }
        },
        pcMessage: {
          root: {
            class: 'dark:text-bg-red-300 dark:bg-red-700/20 dark:border dark:border-red-950'
          }
        },
        content: {
          class: 'dark:text-neutral-200 border-t-none'
        }
      };
    }
  },

  methods: {
    ...mapActions([
      'setProjects'
    ]),

    onFileSelect(event) {
      this.imageUploadCounter = event.files?.length ?? 0;
      this.imageError = null;

      if (this.imageUploadCounter > 1) {
        this.imageError = this.$t('validation.max_one_image');

        return;
      }

      const file = event.files[0];

      if (!file) {
        return;
      }

      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.image = reader.result;
      };
    },

    async createProject({ valid, values, reset }) {
      if (!valid) {
        return;
      }

      try {
        this.loading = true;

        const payload = {
          ...values,
          image: this.image
        };

        const response = await axios.post('/projects', payload);

        await this.setProjects(response.data.projects);

        this.$toast.add({
          severity: 'success',
          summary: this.$t('toast.project_created'),
          life: 3000
        });

        this.showDialog = false;
        reset();

        this.image = null;
        this.imageUploadCounter = 0;
        this.imageError = null;
      } catch (error) {
        console.error(error);

        this.$toast.add({
          severity: 'error',
          summary: this.$t('toast.generic_error_try_again')
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
